import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { playersData } from "./data/playersData";
import { normalizeString, comparePlayers, findPlayerKey } from "./utils/gameUtils";
import { useAuth } from "./context/AuthContext";
import Header from "./components/Header";
import VideoStage from "./components/VideoStage";
import GuessInput from "./components/GuessInput";
import GuessGrid, { buildGuessRows } from "./components/GuessGrid";
import GameMessage from "./components/GameMessage";
import SignupNudgeModal from "./components/SignupNudgeModal";
import Footer from "./components/Footer";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import GoogleUsernamePage from "./pages/GoogleUsernamePage";
import "./App.css";

const MAX_GUESSES = 6;
const API_BASE = process.env.REACT_APP_API_URL || "";

function useDailyPuzzle() {
  const [puzzle, setPuzzle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchPuzzle() {
      try {
        const res = await fetch(`${API_BASE}/api/puzzles/today`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) {
          setPuzzle({
            puzzleId: "daily",
            playerKey: data.playerKey,
            videoUrl: data.videoUrl,
            goalDetails: data.goalDetails || null,
            puzzleNumber: data.puzzleNumber ?? null,
            date: data.date ?? null,
          });
        }
      } catch {
        if (!cancelled) setPuzzle(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchPuzzle();
    return () => { cancelled = true; };
  }, []);

  return { puzzle, loading };
}

function GamePage() {
  const { puzzle, loading } = useDailyPuzzle();
  const { user, token } = useAuth();

  const answerData = puzzle ? playersData[puzzle.playerKey] : null;
  const playerList = useMemo(() => Object.values(playersData), []);
  const goalDetails = puzzle?.goalDetails || null;

  const [currentGuess, setCurrentGuess] = useState(1);
  const [gameOver, setGameOver] = useState(false);
  const [didWin, setDidWin] = useState(null);
  const [guessEntries, setGuessEntries] = useState([]);
  const [restoringSubmission, setRestoringSubmission] = useState(false);
  const [showNudgeModal, setShowNudgeModal] = useState(false);
  const [giveUpPending, setGiveUpPending] = useState(false);
  const nudgeTriggeredRef = useRef(false);
  const nudgeTimerRef = useRef(null);
  const giveUpContainerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!giveUpPending) return;

    function handleOutsideClick(event) {
      if (
        giveUpContainerRef.current
        && !giveUpContainerRef.current.contains(event.target)
      ) {
        setGiveUpPending(false);
      }
    }

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [giveUpPending]);

  useEffect(() => {
    if (!token || !puzzle) return;

    let cancelled = false;
    setRestoringSubmission(true);

    async function restoreSubmission() {
      try {
        const res = await fetch(`${API_BASE}/api/submissions/today`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const { submission } = await res.json();
        if (cancelled || !submission) return;

        if (submission.totalGuesses > 0) {
          const restored = submission.guesses.map((g) => {
            const directLookup = playersData[g.playerKey];
            const reverseLookupKey = !directLookup ? findPlayerKey(playersData, g.displayName) : null;
            const guessData = directLookup
              || (reverseLookupKey && playersData[reverseLookupKey])
              || { displayName: g.displayName };
            return { guessData, hints: g.hints, rowNum: g.guessNumber };
          });

          setGuessEntries(restored);
          setCurrentGuess(submission.totalGuesses + 1);
        }

        if (submission.isComplete) {
          setGameOver(true);
          setDidWin(submission.didWin);
        }
      } catch {
        // Graceful degradation — start fresh if restore fails
      } finally {
        if (!cancelled) setRestoringSubmission(false);
      }
    }

    restoreSubmission();
    return () => { cancelled = true; };
  }, [token, puzzle]);

  const handleSubmitGuess = (guessData) => {
    if (gameOver || !answerData) return;
    const hints = comparePlayers(guessData, answerData);
    const isCorrect =
      normalizeString(guessData.displayName) === normalizeString(answerData.displayName);

    setGuessEntries((prev) => [
      ...prev,
      { guessData, hints, rowNum: currentGuess },
    ]);

    if (!user && !nudgeTriggeredRef.current && guessEntries.length === 0) {
      nudgeTriggeredRef.current = true;
      nudgeTimerRef.current = setTimeout(() => setShowNudgeModal(true), 2000);
    }

    if (isCorrect) {
      setGameOver(true);
      setDidWin(true);
    } else if (currentGuess >= MAX_GUESSES) {
      setGameOver(true);
      setDidWin(false);
    } else {
      setCurrentGuess((c) => c + 1);
    }

    if (token) {
      const playerKey = findPlayerKey(playersData, guessData.displayName);
      fetch(`${API_BASE}/api/submissions/guess`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          playerKey: playerKey || guessData.displayName,
          displayName: guessData.displayName,
          hints,
        }),
      }).catch(() => {});
    }
  };

  const handleGiveUp = useCallback(() => {
    if (gameOver) return;
    if (!giveUpPending) {
      setGiveUpPending(true);
      return;
    }
    setGameOver(true);
    setDidWin(false);
    setGiveUpPending(false);

    if (token) {
      fetch(`${API_BASE}/api/submissions/give-up`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
  }, [gameOver, giveUpPending, token]);

  function formatPuzzleDate(dateStr) {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-");
    return `${m}/${d}/${y.slice(2)}`;
  }

  const guessedPlayers = useMemo(
    () => guessEntries.map((e) => e.guessData.displayName),
    [guessEntries]
  );

  const gridRows = useMemo(
    () => buildGuessRows(guessEntries, comparePlayers),
    [guessEntries]
  );

  if (loading || restoringSubmission) {
    return (
      <main className="main-content">
        <div className="game-container">
          <div className="message-area">Loading today's puzzle...</div>
        </div>
      </main>
    );
  }

  if (!puzzle || !answerData) {
    return (
      <main className="main-content">
        <div className="game-container">
          <div className="message-area game-message-lose">
            <div className="game-message-heading lose">Error Loading Game</div>
            Unable to load game data. Please refresh the page or try again later.
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="main-content">
      <div className="game-container">
        <div className="puzzle-meta-row">
          <div className="puzzle-meta-left">
            {puzzle.puzzleNumber != null && (
              <div className="puzzle-meta-number">#{puzzle.puzzleNumber}</div>
            )}
            {puzzle.date && (
              <div className="puzzle-meta-date">{formatPuzzleDate(puzzle.date)}</div>
            )}
          </div>

          {!gameOver && (
            <div className="puzzle-meta-right" ref={giveUpContainerRef}>
              <button
                className={`give-up-btn${giveUpPending ? " give-up-confirm" : ""}`}
                onClick={handleGiveUp}
              >
                {giveUpPending ? "Confirm" : "Give up"}{" "}
                <span className="give-up-x" aria-hidden="true">&times;</span>
              </button>
              {giveUpPending && (
                <div className="give-up-tooltip">
                  <div className="give-up-tooltip-title">Are you sure?</div>
                  <div className="give-up-tooltip-body">
                    You won't be able to continue the game if you give up.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <VideoStage
          videoUrl={puzzle.videoUrl}
          showSketch={!gameOver}
          onLoaded={() => {}}
        />

        {/* <div className="video-disclaimer">
          Video clips are from{" "}
          <a
            href="https://uefa.tv"
            target="_blank"
            rel="noopener noreferrer"
          >
            uefa.tv
          </a>
          , not affiliated with Goal Guesser
        </div> */}

        <GuessInput
          playerList={playerList}
          guessedPlayers={guessedPlayers}
          onSubmitGuess={handleSubmitGuess}
          disabled={gameOver}
        />

        {gameOver && didWin !== null && (
          <GameMessage
            didWin={didWin}
            answerName={answerData.displayName}
            goalDetails={goalDetails}
          />
        )}

        <GuessGrid rows={gridRows} />
      </div>

      {!user && (
        <SignupNudgeModal
          show={showNudgeModal}
          onClose={() => setShowNudgeModal(false)}
        />
      )}
    </main>
  );
}

const CHROME_ROUTES = new Set(["/", "/play"]);

function App() {
  const { pathname } = useLocation();
  const showChrome = CHROME_ROUTES.has(pathname);

  return (
    <div className="app">
      {showChrome && <Header />}
      <Routes>
        <Route path="/" element={<GamePage />} />
        <Route path="/play" element={<Navigate to="/" replace />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/signup/username" element={<GoogleUsernamePage />} />
      </Routes>
      {showChrome && <Footer />}
    </div>
  );
}

export default App;
